import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { IcoCog, IcoRefresh } from "../../../../components/icons";
import type { Question } from "./types";

type QueueSectionProps = {
  questions: Question[];
  questionsPerPeriod: number;
  repeatAfterComplete: boolean;
  onSettingsChange: (settings: {
    questionsPerPeriod: number;
    repeatAfterComplete: boolean;
  }) => void;
};

export function QueueSection({
  questions,
  questionsPerPeriod,
  repeatAfterComplete,
  onSettingsChange,
}: QueueSectionProps) {
  const showSettings = useSignal(false);

  // Get queued questions (non-archived, frequency='queued')
  const queuedQuestions = questions.filter(
    (q) => !q.archived && q.frequency === "queued",
  );

  // Don't show section if no queued questions
  if (queuedQuestions.length === 0) {
    return null;
  }

  // Calculate how many are "currently showing" vs "next up"
  const currentlyShowing = Math.min(questionsPerPeriod, queuedQuestions.length);
  const totalInQueue = queuedQuestions.length;

  // Calculate periods to complete cycle
  const periodsToComplete =
    totalInQueue > 0 && questionsPerPeriod > 0
      ? Math.ceil(totalInQueue / questionsPerPeriod)
      : 0;

  return (
    <>
      <div class="rounded-xl border border-border bg-surface">
        <div class="flex items-center justify-between p-4">
          <div class="flex items-center gap-3">
            <IcoRefresh class="size-5 text-text-muted" />
            <div>
              <h3 class="font-medium text-text">Question Rotation</h3>
              <p class="text-sm text-text-muted">
                Showing {currentlyShowing} of {totalInQueue} questions per
                check-in
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              showSettings.value = true;
            }}
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
          >
            <IcoCog class="size-4" />
            Settings
          </button>
        </div>

        {/* Queue info */}
        <div class="px-4 pb-4 flex flex-wrap gap-4 text-sm">
          <div class="flex items-center gap-2 text-text-muted">
            <span class="font-medium text-text">{periodsToComplete}</span>
            <span>check-ins to complete rotation</span>
          </div>
          <div class="flex items-center gap-2 text-text-muted">
            {repeatAfterComplete ? (
              <>
                <IcoRefresh class="size-4" />
                <span>Repeats after complete</span>
              </>
            ) : (
              <span>Stops after all shown once</span>
            )}
          </div>
        </div>

        {/* How rotation works explainer */}
        <div class="mx-4 mb-4 px-3 py-2.5 rounded-lg bg-surface-muted/50 text-sm text-text-muted space-y-1">
          <p>
            Rotating questions are shown{" "}
            <span class="font-medium text-text">in addition to</span> "every
            check-in" questions.
          </p>
          <p>
            Each check-in picks from{" "}
            <span class="font-medium text-text">different categories</span> (at
            most 1 per category).
          </p>
          <p>
            Each person may see{" "}
            <span class="font-medium text-text">different questions</span> based
            on group targeting.
          </p>
          <p>
            Group-specific questions count{" "}
            <span class="font-medium text-text">within</span> the per-check-in
            limit, not on top of it.
          </p>
        </div>

        {/* Warning if settings don't make sense */}
        {questionsPerPeriod >= totalInQueue && totalInQueue > 1 && (
          <div class="mx-4 mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300">
            All {totalInQueue} questions will show each check-in. Reduce
            "questions per check-in" to enable rotation.
          </div>
        )}

        {totalInQueue === 1 && (
          <div class="mx-4 mb-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300">
            Add more questions with "In rotation" to enable rotation.
          </div>
        )}
      </div>

      {/* Settings modal */}
      {showSettings.value && (
        <QueueSettingsModal
          questionsPerPeriod={questionsPerPeriod}
          repeatAfterComplete={repeatAfterComplete}
          totalQuestions={totalInQueue}
          onSave={(settings) => {
            onSettingsChange(settings);
            showSettings.value = false;
          }}
          onClose={() => {
            showSettings.value = false;
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// Settings Modal
// ============================================================================

type QueueSettingsModalProps = {
  questionsPerPeriod: number;
  repeatAfterComplete: boolean;
  totalQuestions: number;
  onSave: (settings: {
    questionsPerPeriod: number;
    repeatAfterComplete: boolean;
  }) => void;
  onClose: () => void;
};

function QueueSettingsModal({
  questionsPerPeriod,
  repeatAfterComplete,
  totalQuestions,
  onSave,
  onClose,
}: QueueSettingsModalProps) {
  const localQuestionsPerPeriod = useSignal(questionsPerPeriod);
  const localRepeatAfterComplete = useSignal(repeatAfterComplete);

  const periodsToComplete =
    totalQuestions > 0 && localQuestionsPerPeriod.value > 0
      ? Math.ceil(totalQuestions / localQuestionsPerPeriod.value)
      : 0;

  // Handle Escape key at document level
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div class="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div class="relative bg-surface rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 class="text-lg font-semibold text-text mb-4">Rotation Settings</h2>

        {/* Questions per period */}
        <div class="mb-6">
          <span class="block text-sm font-medium text-text-secondary mb-2">
            Questions per check-in
          </span>
          <p class="text-sm text-text-muted mb-3">
            How many rotating questions should appear in each check-in?
          </p>
          <div class="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  localQuestionsPerPeriod.value = n;
                }}
                class={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  localQuestionsPerPeriod.value === n
                    ? "bg-text text-surface"
                    : "bg-surface-muted text-text-muted hover:bg-surface-hover hover:text-text"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <p class="text-sm text-text-muted mt-2">
            {totalQuestions} questions in queue ÷{" "}
            {localQuestionsPerPeriod.value} per check-in = ~{periodsToComplete}{" "}
            check-ins to rotate through all
          </p>
        </div>

        {/* Repeat behavior */}
        <div class="mb-6">
          <span class="block text-sm font-medium text-text-secondary mb-2">
            After showing all questions
          </span>
          <div class="flex flex-col gap-2">
            <label
              class={`flex gap-3 items-start rounded-lg border p-3 cursor-pointer transition-colors ${
                localRepeatAfterComplete.value
                  ? "border-text-subtle bg-surface"
                  : "border-border hover:border-text-subtle"
              }`}
            >
              <input
                type="radio"
                name="repeat"
                checked={localRepeatAfterComplete.value}
                onChange={() => {
                  localRepeatAfterComplete.value = true;
                }}
                class="mt-0.5"
              />
              <span class="flex flex-col">
                <span class="font-medium text-text">Start over</span>
                <span class="text-sm text-text-muted">
                  Repeat the rotation from the beginning
                </span>
              </span>
            </label>
            <label
              class={`flex gap-3 items-start rounded-lg border p-3 cursor-pointer transition-colors ${
                !localRepeatAfterComplete.value
                  ? "border-text-subtle bg-surface"
                  : "border-border hover:border-text-subtle"
              }`}
            >
              <input
                type="radio"
                name="repeat"
                checked={!localRepeatAfterComplete.value}
                onChange={() => {
                  localRepeatAfterComplete.value = false;
                }}
                class="mt-0.5"
              />
              <span class="flex flex-col">
                <span class="font-medium text-text">Stop</span>
                <span class="text-sm text-text-muted">
                  Each question is shown once, then rotation ends
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div class="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onSave({
                questionsPerPeriod: localQuestionsPerPeriod.value,
                repeatAfterComplete: localRepeatAfterComplete.value,
              });
            }}
            class="px-4 py-2 rounded-lg text-sm font-medium bg-text text-surface hover:opacity-90 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
